import type { ReactNode } from 'react';
import React from 'react';
import ProductTaggingFixedBar from '../components/product-tagging-fixed-bar/ProductTaggingFixedBar';
import type { TagPosition } from '../type';
import type { ProductTaggingSearchResult } from '../type';
import {
    productTaggingReducer,
    type ProductTaggingAction,
} from './ProductTaggingReducer';

interface ProductTaggingContextProviderProps {
    children: ReactNode;
    canCreateTag: boolean;
    canDeleteTag: boolean;
    recentlyTaggedProducts: ProductTaggingSearchResult[];
    recentlyTaggedProfessionals: ProductTaggingSearchResult[];
    onAddingProductTag?: () => void;
}
export interface ProductTaggingState {
    isTagging: boolean;
    isDeletingTag: boolean;
    isAddingSupplier: boolean;
    tagPosition: TagPosition | undefined;
    lastAddedTag:
        | Pick<TagPosition, 'pctX' | 'pctY' | 'libraryImageID'>
        | undefined;
    onAddingProductTag?: () => void;
}

const ProductTaggingContext = React.createContext<
    | {
          state: ProductTaggingState;
          dispatch: (action: ProductTaggingAction) => void;
          canCreateTag: boolean;
          canDeleteTag: boolean;
          recentlyTaggedProducts: ProductTaggingSearchResult[];
          recentlyTaggedProfessionals: ProductTaggingSearchResult[];
      }
    | undefined
>(undefined);

export const useProductTagging = () => {
    const context = React.useContext(ProductTaggingContext);

    if (!context) {
        throw new Error('Cannot be used outside ProductTaggingContext');
    }
    return context;
};

export const ProductTaggingContextProvider = ({
    children,
    canCreateTag = false,
    canDeleteTag = false,
    recentlyTaggedProducts,
    recentlyTaggedProfessionals,
    onAddingProductTag,
}: ProductTaggingContextProviderProps) => {
    const [state, dispatch] = React.useReducer(productTaggingReducer, {
        isTagging: false,
        isDeletingTag: false,
        isAddingSupplier: false,
        tagPosition: undefined,
        lastAddedTag: undefined,
        onAddingProductTag: onAddingProductTag,
    });

    return (
        <ProductTaggingContext.Provider
            value={{
                state,
                dispatch,
                canCreateTag,
                canDeleteTag,
                recentlyTaggedProducts,
                recentlyTaggedProfessionals,
            }}
        >
            {children}
            {canCreateTag && <ProductTaggingFixedBar />}
        </ProductTaggingContext.Provider>
    );
};
